import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';

const ContractTemplates: React.FC = () => {
    const [templates, setTemplates] = useState<any[]>([]);
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState('');
    const [showDeleteModal, setShowDeleteModal] = useState(false);
    const [templateToDelete, setTemplateToDelete] = useState<number | null>(null);

    useEffect(() => {
        fetchTemplates();
    }, []);

    const fetchTemplates = async () => {
        setIsLoading(true);
        setError('');
        try {
            const token = localStorage.getItem('token');
            const response = await axios.get(`${process.env.REACT_APP_API_BASE_URL}/api/ContractTemplates`, {
                headers: { Authorization: `Bearer ${token}` },
            });
            setTemplates(response.data);
        } catch (error: any) {
            setError('Failed to load contract templates.');
        } finally {
            setIsLoading(false);
        }
    };

    const deleteTemplate = async (id: number) => {
        try {
            const token = localStorage.getItem('token');
            await axios.delete(`${process.env.REACT_APP_API_BASE_URL}/api/ContractTemplates/${id}`, {
                headers: { Authorization: `Bearer ${token}` },
            });
            setTemplates(templates.filter(template => template.id !== id));
            alert('Contract template deleted successfully!');
        } catch (error: any) {
            setError(error.response?.data || 'Failed to delete contract template.');
        } finally {
            setShowDeleteModal(false);
            setTemplateToDelete(null);
        }
    };

    const handleDeleteClick = (id: number) => {
        setTemplateToDelete(id);
        setShowDeleteModal(true);
    };

    const confirmDelete = () => {
        if (templateToDelete !== null) {
            deleteTemplate(templateToDelete);
        }
    };

    return (
        <div className="container mt-5">
            <h2 className="mb-4 text-center">Contract Templates</h2>
            {error && <div className="alert alert-danger">{error}</div>}
            {isLoading && <div className="alert alert-info">Loading...</div>}
            <div className="row justify-content-center">
                <div className="col-md-8">
                    <div className="card shadow-sm">
                        <div className="card-header bg-primary text-white d-flex justify-content-between align-items-center">
                            <h4 className="mb-0">Templates ({templates.length})</h4>
                            <Link to="/edit-contract-template/new" className="btn btn-light btn-sm">
                                New Contract Template
                            </Link>
                        </div>
                        <div className="card-body">
                            {templates.length === 0 && !isLoading && !error ? (
                                <p className="text-muted text-center">No contract templates created yet.</p>
                            ) : (
                                <ul className="list-group list-group-flush">
                                    {templates.map((template) => (
                                        <li
                                            key={template.id}
                                            className="list-group-item d-flex justify-content-between align-items-center"
                                        >
                                            <span>{template.name}</span>
                                            <div>
                                                <Link
                                                    to={`/edit-contract-template/${template.id}`}
                                                    className="btn btn-outline-primary btn-sm me-2"
                                                >
                                                    Edit
                                                </Link>
                                                <button
                                                    onClick={() => handleDeleteClick(template.id)}
                                                    className="btn btn-outline-danger btn-sm"
                                                >
                                                    Delete
                                                </button>
                                            </div>
                                        </li>
                                    ))}
                                </ul>
                            )}
                        </div>
                    </div>
                </div>
            </div>

            {/* Delete Confirmation Modal */}
            {showDeleteModal && (
                <div className="modal fade show d-block" tabIndex={-1} style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}>
                    <div className="modal-dialog">
                        <div className="modal-content">
                            <div className="modal-header">
                                <h5 className="modal-title">Confirm Deletion</h5>
                                <button
                                    type="button"
                                    className="btn-close"
                                    onClick={() => setShowDeleteModal(false)}
                                ></button>
                            </div>
                            <div className="modal-body">
                                <p>Are you sure you want to delete this contract template?</p>
                            </div>
                            <div className="modal-footer">
                                <button
                                    type="button"
                                    className="btn btn-secondary"
                                    onClick={() => setShowDeleteModal(false)}
                                >
                                    Cancel
                                </button>
                                <button
                                    type="button"
                                    className="btn btn-danger"
                                    onClick={confirmDelete}
                                >
                                    Delete
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default ContractTemplates;